import { useState } from 'react'
import { Calculator, LogOut, Pencil } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import useStore from '@/stores/useStore'
import { CENSUS_MODES } from '@/lib/demoEntitlements'
import { describeEstimatorInputs } from '@/lib/estimator'
import EstimatorDialog from './EstimatorDialog'

// Always-visible amber bar while Estimate Mode is on, so a synthetic census can
// never be mistaken for real client data. Exiting restores the stashed
// workspace exactly as it was.
export default function EstimateModeBanner() {
  const workspaceMode = useStore((state) => state.workspaceMode)
  const inputs = useStore((state) => state.estimator?.inputs)
  const exitEstimateMode = useStore((state) => state.exitEstimateMode)
  const [dialogOpen, setDialogOpen] = useState(false)

  if (workspaceMode !== 'estimate') return null

  // Say what actually came back – someone who started an estimate during
  // onboarding may never have seen the example census they "restore" to.
  const handleExit = () => {
    const stash = useStore.getState().stashedActual
    const count = stash?.employees?.length ?? 0
    const restoredExample = stash?.censusMode === CENSUS_MODES.EXAMPLE
    exitEstimateMode()
    toast.success('Estimate ended', {
      description: count === 0
        ? 'Back to an empty calculator – add your employee list to continue.'
        : restoredExample
        ? `Restored the sample employee list (${count} employees).`
        : `Restored your employee list (${count} employee${count === 1 ? '' : 's'}).`,
    })
  }

  return (
    <div className="shrink-0 border-b border-amber-500/50 bg-amber-500/10 px-3 py-1.5 text-amber-900 dark:text-amber-200">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
        <span className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wide">
          <Calculator className="h-3.5 w-3.5" /> Prospect mode
        </span>
        <span className="min-w-0 flex-1 truncate opacity-80" title={describeEstimatorInputs(inputs)}>
          Sample employees from rough assumptions – {describeEstimatorInputs(inputs)}. Not real client data.
        </span>
        <span className="flex items-center gap-1">
          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-inherit hover:bg-amber-500/20" onClick={() => setDialogOpen(true)}>
            <Pencil className="mr-1 h-3 w-3" /> Edit assumptions
          </Button>
          <Button type="button" variant="ghost" size="sm" className="h-6 px-2 text-xs text-inherit hover:bg-amber-500/20" onClick={handleExit}>
            <LogOut className="mr-1 h-3 w-3" /> Exit &amp; restore real data
          </Button>
        </span>
      </div>
      <EstimatorDialog open={dialogOpen} onOpenChange={setDialogOpen} />
    </div>
  )
}
